<template>
  <article>
    <AppBanner
      title="Events"
      title-alignment="text-left flex-row max-w-[1600px]"
    >
      <AppLink :to="{name: 'app_admin_event_new'}">
        <AppButton
          type="button"
          class="btn-solid btn-secondary rounded-sm px-3 md:px-5 whitespace-nowrap"
        >
          <FontAwesomeIcon :icon="faPlus" />
          Add New
        </AppButton>
      </AppLink>
    </AppBanner>

    <div class="max-w-[1600px] mx-auto container-padding flex flex-col gap-2 mt-8">
      <div class="flex items-center md:max-w-1/3 min-w-70 mb-4">
        <input
          v-model="searchParams.search"
          type="text"
          class="relative border-gray rounded-r-none focus:border-secondary border-r-0 focus:ring-0"
          placeholder="Search for event"
          @keyup.enter="handleSearch"
        >

        <AppButton
          class="btn btn-solid btn-secondary rounded-s-none rounded-e h-10 lg:h-11"
          @click="handleSearch"
        >
          Search
        </AppButton>

        <div
          v-if="showSearchResults && searchResults && searchResults.length"
          class="absolute top-14 bg-white flex flex-col divide-y divide-secondary-subtle rounded border border-gray-200 shadow-sm w-full"
        >
          <template
            v-for="result in searchResults"
            :key="result.id"
          >
            <AppLink
              class="px-4 py-2 hover:bg-secondary-subtle"
              :to="{
                name: 'app_event_show',
                params: {slug: result.slug}}"
            >
              {{ result.name }}
            </AppLink>
          </template>
        </div>
      </div>

      <AppTable
        :loading="loading"
        :if-looped-items-exist="events?.length > 0"
      >
        <template #headings>
          <th class="sr-only" />
          <th>Name</th>
          <th>Date</th>
          <th>Location</th>
          <th>Photos</th>
          <th>Revenue</th>
          <th class="sr-only" />
        </template>

        <template #rows>
          <tr
            v-for="event in events"
            :key="event.id"
          >
            <td>
              <img
                loading="lazy"
                class="h-full p-0 object-cover object-center cursor-pointer"
                :src="event.eventImage"
                alt="event image"
              >
            </td>

            <td>
              <AppLink
                class="cursor-pointer font-semibold underline text-secondary"
                :to="{ name: 'app_admin_event_show', params: { slug: event.slug } }"
              >
                {{ event.name }}
              </AppLink>
            </td>

            <td>{{ dateFormatter.formatShortDate(event.date) || '-' }}</td>
            <td>{{ event.location || '-' }}</td>
            <td>{{ event.photoCount || '0' }}</td>
            <td>{{ string.formatCurrency(event.report.totalSalesAmount) }}</td>
            <td>
              <div class="table-dropdown">
                <AppDropdown class="admin-table-dropdown">
                  <AppDropdownItem :to="{ name: 'app_admin_event_edit', params: { slug: event.slug } }">
                    Edit
                  </AppDropdownItem>

                  <AppDropdownItem @click="eventToDelete = event">
                    Delete
                  </AppDropdownItem>
                </AppDropdown>
              </div>
            </td>
          </tr>
        </template>
      </AppTable>

      <AdminPagination
        target-id="topOfPage"
        :items-per-page="paginationData?.itemsPerPage"
        :current-page="paginationData?.page"
        :total-items="paginationData?.total"
        @change="handlePageChange"
      />
    </div>

    <Transition name="fade">
      <DeleteModal
        v-if="eventToDelete"
        title="Event"
        text="Are you sure you want to delete this event?"
        :entity-name="eventToDelete.name"
        :error="error"
        :processing="isDeleting"
        @delete="handleDelete(eventToDelete.slug)"
        @close="eventToDelete = null"
      />
    </Transition>
  </article>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import {
  AppDropdown,
  AppDropdownItem,
  AppBanner,
  AppButton,
  AppLink,
} from '@/components/app';

import {
  AdminPagination,
} from '@/components/app/admin';

import { useHttp, useString } from '@/composables';
import { HttpStatusCode } from 'axios';
import { useDateFormatter } from '@/composables';
import { DeleteModal } from '@/components/modals';
import { AppTable } from '@/components/app';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

const dateFormatter = useDateFormatter();
const string = useString();

const events = ref([]);

const errors = ref(null);
const loading = ref(false);

const eventToDelete = ref(null);
const isDeleting = ref(false);
const error = ref(null);

onMounted(async () => {
  await loadEvents();
});

const searchResults = ref([]);
const showSearchResults = ref(true);

const searchParams = reactive({
  page: 1,
  search: null,
});

const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
  total: 0,
});

const loadEvents = async () => {
  loading.value = true;

  const response = await useHttp().get('/events', searchParams);

  if (response.statusCode === HttpStatusCode.Ok) {
    events.value = response.data.items;
    paginationData.value = response.data.pagination;
  } else {
    errors.value = response.errors;
  }

  loading.value = false;
};

const handleSearch = () => {
  searchParams.page = 1;
  events.value = [];
  showSearchResults.value = false;

  loadEvents();
};

const handlePageChange = page => {
  searchParams.page = page;

  loadEvents();
};

const handleDelete = async (slug) => {
  isDeleting.value = true;

  const response = await useHttp().delete(`/events/${slug}`);

  isDeleting.value = false;
  eventToDelete.value = null;

  if (response.statusCode === HttpStatusCode.Ok) {
    await loadEvents();
  } else {
    error.value = 'Something went wrong. Please try again';
  }
};
</script>
